/**
 * AI 답변 포맷(synthesis-format) API — /api/admin/ai-rulesets/synthesis-format.
 * BFF → chat-server proxy → orchestrator rulesets-admin(SSOT, 검증·핫리로드). install: ai-answer-format-by-category.
 */
import { apiClient } from './client';

export interface SynthesisToneEntry {
  description?: string;
  instructions: string[];
  max_sentences?: number | null;
  // 'data_table' = LLM 합성 없이 도구 결과를 고정 표로 결정적 렌더(같은 데이터 → 같은 출력).
  // [직교 2축] 출력형식은 formats 가 담당 — 본 필드는 legacy tone_by_tool 경로용.
  output_format?: 'markdown' | 'html' | 'data_table';
  /** [직교 2축] 격식 — 톤(말투)축 전용. */
  formality?: 'formal' | 'neutral' | 'friendly';
}
export interface SynthesisFormatHints {
  list_threshold?: number;
  table_threshold?: number;
  callout_for_warnings?: boolean;
  code_block_for_ids?: boolean;
}
/** [직교 2축] 포맷 라이브러리 엔트리 — 구조(STRUCTURE) 전용. */
export interface SynthesisFormatEntry {
  description?: string;
  instructions?: string[];
  output_format?: 'markdown' | 'html' | 'data_table';
  format_hints?: SynthesisFormatHints;
  /** [표시 전용] 목업·예제 시각 모양. 미지정 시 추론. */
  mock?: 'table' | 'list' | 'report' | 'detailed_para' | 'data_table' | 'diagram' | 'gantt' | 'mindmap' | 'sequence' | 'pie';
  /** 색상 팔레트(hex) — 차트·다이어그램·HTML 강조에 순서대로 사용. 미지정 시 색 지정 없음. */
  palette?: string[];
  /** 외부 보더(표·박스·리포트 바깥 테두리) 색상(hex). 미지정 시 기본 테두리. */
  borderColor?: string;
  /** 내부 보더(표·객체 셀 구분선) 색상(hex). 미지정 시 기본. */
  innerBorderColor?: string;
}
export interface SynthesisCommon {
  language?: string;
  forbid_phrases?: string[];
  always_cite_fields?: string[];
  extra_instructions?: string[];
}
/** [직교 2축] 카테고리/도구 → (톤,포맷) 독립 매핑. */
export interface StyleMapping {
  tone?: string;
  format?: string;
}
export interface SynthesisFormat {
  version: number;
  description?: string;
  common?: SynthesisCommon;
  /** 톤(말투) 라이브러리. */
  tones: Record<string, SynthesisToneEntry>;
  /** [직교 2축] 포맷(구조) 라이브러리. */
  formats?: Record<string, SynthesisFormatEntry>;
  format_hints?: SynthesisFormatHints;
  /** [직교 2축] 도구 prefix(카테고리+".") → (톤,포맷) 매핑. */
  style_by_tool?: Record<string, StyleMapping>;
  /** [DEPRECATED·하위호환] 도구 prefix → 톤(프로파일) 키. style_by_tool 미매칭 시 fallback. */
  tone_by_tool?: Record<string, string>;
}

export interface SynthesisFormatGet {
  content: SynthesisFormat | null;
  mtime: string | null;
}

export async function getSynthesisFormat(): Promise<SynthesisFormatGet> {
  const { data } = await apiClient.get<SynthesisFormatGet>('/admin/ai-rulesets/synthesis-format');
  return data;
}

export interface PutFormatResult {
  ok?: boolean;
  mtime?: string;
  note?: string;
  error?: string;
  message?: string;
}

export async function putSynthesisFormat(content: SynthesisFormat, baseMtime: string | null): Promise<PutFormatResult> {
  const { data } = await apiClient.put<PutFormatResult>('/admin/ai-rulesets/synthesis-format', { content, baseMtime });
  return data;
}

// ── AI 프리셋 생성 (단일 톤/포맷 엔트리, 저장 안 함 — 검토용) ──
export interface GenerateStyleResult {
  ok?: boolean;
  kind?: 'tone' | 'format';
  entry?: SynthesisToneEntry & SynthesisFormatEntry;
  rationale?: string;
  error?: string;
  message?: string;
  validation_error?: string;
}
export async function generateStyleEntry(kind: 'tone' | 'format', brief: string): Promise<GenerateStyleResult> {
  // LLM 호출 — 긴 타임아웃.
  const { data } = await apiClient.post<GenerateStyleResult>(
    '/admin/ai-rulesets/generate-style-entry', { kind, brief }, { timeout: 70_000 },
  );
  return data;
}
